import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, Tabs, Tab, Box,
  TextField, Button, IconButton, Table, TableHead, TableBody,
  TableRow, TableCell, Select, MenuItem, FormControl, InputLabel,
  Alert, Switch, FormControlLabel, Chip,
} from '@mui/material';
import { Add, Delete, Edit, Save, Cancel } from '@mui/icons-material';
import { RendimientoService } from '../services/rendimientoService';
import type { Actividad, Labor, Consumidor, GrupoTrabajo, CostoActividad } from '../types/rendimiento';

interface Props {
  open: boolean;
  onClose: () => void;
}

const MasterDataDialog: React.FC<Props> = ({ open, onClose }) => {
  const [tab, setTab] = useState(0);
  const [actividades, setActividades] = useState<Actividad[]>([]);
  const [labores, setLabores] = useState<Labor[]>([]);
  const [consumidores, setConsumidores] = useState<Consumidor[]>([]);
  const [grupos, setGrupos] = useState<GrupoTrabajo[]>([]);
  const [costos, setCostos] = useState<CostoActividad[]>([]);
  const [costoActividadId, setCostoActividadId] = useState<number | ''>('');
  const [msg, setMsg] = useState<string | null>(null);

  // Edit forms
  const [editAct, setEditAct] = useState<Partial<Actividad> | null>(null);
  const [editLab, setEditLab] = useState<Partial<Labor> | null>(null);
  const [editCon, setEditCon] = useState<Partial<Consumidor> | null>(null);
  const [editGrp, setEditGrp] = useState<Partial<GrupoTrabajo> | null>(null);
  const [editCosto, setEditCosto] = useState<Partial<CostoActividad> | null>(null);

  useEffect(() => {
    if (open) loadAll();
  }, [open]);

  const loadAll = async () => {
    try {
      const [a, l, c, g] = await Promise.all([
        RendimientoService.getActividades(),
        RendimientoService.getLabores(),
        RendimientoService.getConsumidores(),
        RendimientoService.getGruposTrabajo(),
      ]);
      setActividades(a);
      setLabores(l);
      setConsumidores(c);
      setGrupos(g);
    } catch { /* handled by interceptor */ }
  };

  // ── Actividades CRUD ──
  const saveActividad = async () => {
    if (!editAct?.codigo || !editAct?.descripcion) return;
    try {
      await RendimientoService.saveActividad(editAct);
      setEditAct(null);
      setMsg('Actividad guardada');
      const a = await RendimientoService.getActividades();
      setActividades(a);
    } catch { /* interceptor */ }
  };

  const deleteActividad = async (id: number) => {
    try {
      await RendimientoService.deleteActividad(id);
      setActividades(prev => prev.filter(a => a.idActividad !== id));
    } catch { /* interceptor */ }
  };

  // ── Labores CRUD ──
  const saveLabor = async () => {
    if (!editLab?.codigo || !editLab?.descripcion || !editLab?.idActividad) return;
    try {
      await RendimientoService.saveLabor(editLab);
      setEditLab(null);
      setMsg('Labor guardada');
      const l = await RendimientoService.getLabores();
      setLabores(l);
    } catch { /* interceptor */ }
  };

  const deleteLabor = async (id: number) => {
    try {
      await RendimientoService.deleteLabor(id);
      setLabores(prev => prev.filter(l => l.idLabor !== id));
    } catch { /* interceptor */ }
  };

  // ── Consumidores CRUD ──
  const saveConsumidor = async () => {
    if (!editCon?.codigo || !editCon?.descripcion) return;
    try {
      await RendimientoService.saveConsumidor(editCon);
      setEditCon(null);
      setMsg('Consumidor guardado');
      const c = await RendimientoService.getConsumidores();
      setConsumidores(c);
    } catch { /* interceptor */ }
  };

  const deleteConsumidor = async (id: number) => {
    try {
      await RendimientoService.deleteConsumidor(id);
      setConsumidores(prev => prev.filter(c => c.idConsumidor !== id));
    } catch { /* interceptor */ }
  };

  // ── Grupos CRUD ──
  const saveGrupo = async () => {
    if (!editGrp?.codigo || !editGrp?.descripcion) return;
    try {
      await RendimientoService.saveGrupoTrabajo(editGrp);
      setEditGrp(null);
      setMsg('Grupo guardado');
      const g = await RendimientoService.getGruposTrabajo();
      setGrupos(g);
    } catch { /* interceptor */ }
  };

  const deleteGrupo = async (id: number) => {
    try {
      await RendimientoService.deleteGrupoTrabajo(id);
      setGrupos(prev => prev.filter(g => g.idGrupoTrabajo !== id));
    } catch { /* interceptor */ }
  };

  // ── Costos CRUD ──
  const loadCostos = async (idActividad: number) => {
    try {
      const c = await RendimientoService.getCostosActividad(idActividad);
      setCostos(c);
    } catch { /* interceptor */ }
  };

  const saveCosto = async () => {
    if (!editCosto?.idActividad || !editCosto?.idLabor || !editCosto?.periodo) return;
    try {
      await RendimientoService.saveCostoActividad(editCosto);
      setEditCosto(null);
      setMsg('Costo guardado');
      if (costoActividadId) await loadCostos(costoActividadId as number);
    } catch { /* interceptor */ }
  };

  const deleteCosto = async (id: number) => {
    try {
      await RendimientoService.deleteCostoActividad(id);
      setCostos(prev => prev.filter(c => c.idCostoActividad !== id));
    } catch { /* interceptor */ }
  };

  const handleCostoActividadChange = (idActividad: number) => {
    setCostoActividadId(idActividad);
    setEditCosto(null);
    loadCostos(idActividad);
  };

  const laboresForCosto = labores.filter(l => l.idActividad === costoActividadId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Datos Maestros - Rendimiento</DialogTitle>
      <DialogContent>
        {msg && <Alert severity="success" onClose={() => setMsg(null)} sx={{ mb: 1 }}>{msg}</Alert>}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
          <Tab label="Actividades" />
          <Tab label="Labores" />
          <Tab label="Consumidores" />
          <Tab label="Grupos Trabajo" />
          <Tab label="Costos Actividad" />
        </Tabs>

        {/* Tab 0: Actividades */}
        {tab === 0 && (
          <Box>
            <Box display="flex" gap={1} mb={1} flexWrap="wrap">
              <TextField size="small" label="Codigo" value={editAct?.codigo || ''} sx={{ width: 100 }}
                onChange={e => setEditAct(prev => ({ ...prev, codigo: e.target.value }))} />
              <TextField size="small" label="Descripcion" value={editAct?.descripcion || ''} sx={{ flex: 1, minWidth: 200 }}
                onChange={e => setEditAct(prev => ({ ...prev, descripcion: e.target.value }))} />
              <TextField size="small" label="Nombre Corto" value={editAct?.nombreCorto || ''} sx={{ width: 130 }}
                onChange={e => setEditAct(prev => ({ ...prev, nombreCorto: e.target.value }))} />
              <TextField size="small" label="Cod. Alterno" value={editAct?.codigoAlterno || ''} sx={{ width: 110 }}
                onChange={e => setEditAct(prev => ({ ...prev, codigoAlterno: e.target.value }))} />
              <FormControlLabel label="Rend." control={
                <Switch size="small" checked={editAct?.esActividadRendimiento ?? true}
                  onChange={e => setEditAct(prev => ({ ...prev, esActividadRendimiento: e.target.checked }))} />
              } />
              <FormControlLabel label="Linea Prod." control={
                <Switch size="small" checked={editAct?.esRendimientoLineaProduccion ?? false}
                  onChange={e => setEditAct(prev => ({ ...prev, esRendimientoLineaProduccion: e.target.checked }))} />
              } />
              <Button variant="contained" size="small" startIcon={<Save />} onClick={saveActividad}>
                Guardar
              </Button>
              {editAct?.idActividad ? (
                <Button size="small" onClick={() => setEditAct(null)} startIcon={<Cancel />}>Cancelar</Button>
              ) : null}
            </Box>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Codigo</TableCell><TableCell>Descripcion</TableCell>
                <TableCell>Nombre Corto</TableCell><TableCell>Cod. Alt.</TableCell>
                <TableCell>Rend.</TableCell><TableCell>Linea Prod.</TableCell>
                <TableCell>Activo</TableCell><TableCell width={80}>Acc.</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {actividades.map(a => (
                  <TableRow key={a.idActividad}>
                    <TableCell>{a.codigo}</TableCell>
                    <TableCell>{a.descripcion}</TableCell>
                    <TableCell>{a.nombreCorto}</TableCell>
                    <TableCell>{a.codigoAlterno}</TableCell>
                    <TableCell>{a.esActividadRendimiento ? <Chip label="Si" size="small" color="success" /> : <Chip label="No" size="small" />}</TableCell>
                    <TableCell>{a.esRendimientoLineaProduccion ? <Chip label="Si" size="small" color="info" /> : <Chip label="No" size="small" />}</TableCell>
                    <TableCell>{a.activo ? 'Si' : 'No'}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setEditAct(a)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteActividad(a.idActividad)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Tab 1: Labores */}
        {tab === 1 && (
          <Box>
            <Box display="flex" gap={1} mb={1} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Actividad</InputLabel>
                <Select label="Actividad" value={editLab?.idActividad || ''}
                  onChange={e => setEditLab(prev => ({ ...prev, idActividad: e.target.value as number }))}>
                  {actividades.map(a => <MenuItem key={a.idActividad} value={a.idActividad}>{a.codigo} - {a.descripcion}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="Codigo" value={editLab?.codigo || ''} sx={{ width: 100 }}
                onChange={e => setEditLab(prev => ({ ...prev, codigo: e.target.value }))} />
              <TextField size="small" label="Descripcion" value={editLab?.descripcion || ''} sx={{ flex: 1, minWidth: 150 }}
                onChange={e => setEditLab(prev => ({ ...prev, descripcion: e.target.value }))} />
              <TextField size="small" label="U.Medida" value={editLab?.unidadMedida || 'HORAS'} sx={{ width: 90 }}
                onChange={e => setEditLab(prev => ({ ...prev, unidadMedida: e.target.value }))} />
              <TextField size="small" label="Costo/Hora" type="number" value={editLab?.costoHora ?? 0} sx={{ width: 100 }}
                onChange={e => setEditLab(prev => ({ ...prev, costoHora: parseFloat(e.target.value) || 0 }))} />
              <TextField size="small" label="Factor" type="number" value={editLab?.factor ?? 0} sx={{ width: 80 }}
                onChange={e => setEditLab(prev => ({ ...prev, factor: parseFloat(e.target.value) || 0 }))} />
              <TextField size="small" label="Avance St" type="number" value={editLab?.avanceSt ?? 0} sx={{ width: 90 }}
                onChange={e => setEditLab(prev => ({ ...prev, avanceSt: parseFloat(e.target.value) || 0 }))} />
              <TextField size="small" label="Med. Rend." value={editLab?.medidaRendimiento || ''} sx={{ width: 100 }}
                onChange={e => setEditLab(prev => ({ ...prev, medidaRendimiento: e.target.value }))} />
              <TextField size="small" label="V.Venta/Hora" type="number" value={editLab?.ventaHora ?? 0} sx={{ width: 110 }}
                onChange={e => setEditLab(prev => ({ ...prev, ventaHora: parseFloat(e.target.value) || 0 }))} />
              <Button variant="contained" size="small" startIcon={<Save />} onClick={saveLabor}>Guardar</Button>
              {editLab?.idLabor ? (
                <Button size="small" onClick={() => setEditLab(null)} startIcon={<Cancel />}>Cancelar</Button>
              ) : null}
            </Box>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead><TableRow>
                  <TableCell>Actividad</TableCell><TableCell>Codigo</TableCell>
                  <TableCell>Descripcion</TableCell><TableCell>U.Medida</TableCell>
                  <TableCell align="right">Costo/Hora</TableCell><TableCell align="right">Factor</TableCell>
                  <TableCell align="right">Avance St</TableCell><TableCell>Med.Rend.</TableCell>
                  <TableCell align="right">V.Venta/H</TableCell>
                  <TableCell width={80}>Acc.</TableCell>
                </TableRow></TableHead>
                <TableBody>
                  {labores.map(l => (
                    <TableRow key={l.idLabor}>
                      <TableCell>{l.actividad?.codigo}</TableCell>
                      <TableCell>{l.codigo}</TableCell>
                      <TableCell>{l.descripcion}</TableCell>
                      <TableCell>{l.unidadMedida}</TableCell>
                      <TableCell align="right">{l.costoHora?.toFixed(2)}</TableCell>
                      <TableCell align="right">{l.factor?.toFixed(2)}</TableCell>
                      <TableCell align="right">{l.avanceSt?.toFixed(2)}</TableCell>
                      <TableCell>{l.medidaRendimiento}</TableCell>
                      <TableCell align="right">{l.ventaHora?.toFixed(2)}</TableCell>
                      <TableCell>
                        <IconButton size="small" onClick={() => setEditLab(l)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteLabor(l.idLabor)}><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </Box>
        )}

        {/* Tab 2: Consumidores */}
        {tab === 2 && (
          <Box>
            <Box display="flex" gap={1} mb={1}>
              <TextField size="small" label="Codigo" value={editCon?.codigo || ''}
                onChange={e => setEditCon(prev => ({ ...prev, codigo: e.target.value }))} />
              <TextField size="small" label="Descripcion" fullWidth value={editCon?.descripcion || ''}
                onChange={e => setEditCon(prev => ({ ...prev, descripcion: e.target.value }))} />
              <Button variant="contained" size="small" startIcon={<Save />} onClick={saveConsumidor}>Guardar</Button>
              {editCon?.idConsumidor ? (
                <Button size="small" onClick={() => setEditCon(null)} startIcon={<Cancel />}>Cancelar</Button>
              ) : null}
            </Box>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Codigo</TableCell><TableCell>Descripcion</TableCell>
                <TableCell width={80}>Acc.</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {consumidores.map(c => (
                  <TableRow key={c.idConsumidor}>
                    <TableCell>{c.codigo}</TableCell>
                    <TableCell>{c.descripcion}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setEditCon(c)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteConsumidor(c.idConsumidor)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Tab 3: Grupos Trabajo */}
        {tab === 3 && (
          <Box>
            <Box display="flex" gap={1} mb={1}>
              <TextField size="small" label="Codigo" value={editGrp?.codigo || ''}
                onChange={e => setEditGrp(prev => ({ ...prev, codigo: e.target.value }))} />
              <TextField size="small" label="Descripcion" fullWidth value={editGrp?.descripcion || ''}
                onChange={e => setEditGrp(prev => ({ ...prev, descripcion: e.target.value }))} />
              <Button variant="contained" size="small" startIcon={<Save />} onClick={saveGrupo}>Guardar</Button>
              {editGrp?.idGrupoTrabajo ? (
                <Button size="small" onClick={() => setEditGrp(null)} startIcon={<Cancel />}>Cancelar</Button>
              ) : null}
            </Box>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>Codigo</TableCell><TableCell>Descripcion</TableCell>
                <TableCell width={80}>Acc.</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {grupos.map(g => (
                  <TableRow key={g.idGrupoTrabajo}>
                    <TableCell>{g.codigo}</TableCell>
                    <TableCell>{g.descripcion}</TableCell>
                    <TableCell>
                      <IconButton size="small" onClick={() => setEditGrp(g)}><Edit fontSize="small" /></IconButton>
                      <IconButton size="small" color="error" onClick={() => deleteGrupo(g.idGrupoTrabajo)}><Delete fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        {/* Tab 4: Costos de Actividad */}
        {tab === 4 && (
          <Box>
            <Box display="flex" gap={1} mb={2}>
              <FormControl size="small" sx={{ minWidth: 300 }}>
                <InputLabel>Actividad</InputLabel>
                <Select label="Actividad" value={costoActividadId}
                  onChange={e => handleCostoActividadChange(e.target.value as number)}>
                  {actividades.map(a => <MenuItem key={a.idActividad} value={a.idActividad}>{a.codigo} - {a.descripcion}</MenuItem>)}
                </Select>
              </FormControl>
            </Box>
            {costoActividadId && (
              <>
                <Box display="flex" gap={1} mb={1} flexWrap="wrap">
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Labor</InputLabel>
                    <Select label="Labor" value={editCosto?.idLabor || ''}
                      onChange={e => setEditCosto(prev => ({ ...prev, idActividad: costoActividadId as number, idLabor: e.target.value as number }))}>
                      {laboresForCosto.map(l => <MenuItem key={l.idLabor} value={l.idLabor}>{l.codigo} - {l.descripcion}</MenuItem>)}
                    </Select>
                  </FormControl>
                  <TextField size="small" label="Periodo (YYYYMM)" value={editCosto?.periodo || ''} sx={{ width: 140 }}
                    onChange={e => setEditCosto(prev => ({ ...prev, periodo: e.target.value }))} />
                  <TextField size="small" label="Cantidad" type="number" value={editCosto?.cantidad ?? 0} sx={{ width: 100 }}
                    onChange={e => setEditCosto(prev => ({ ...prev, cantidad: parseFloat(e.target.value) || 0 }))} />
                  <TextField size="small" label="U.Medida" value={editCosto?.unidadMedida || 'KLGS'} sx={{ width: 90 }}
                    onChange={e => setEditCosto(prev => ({ ...prev, unidadMedida: e.target.value }))} />
                  <TextField size="small" label="Descr. U.M." value={editCosto?.descripcionUM || ''} sx={{ width: 120 }}
                    onChange={e => setEditCosto(prev => ({ ...prev, descripcionUM: e.target.value }))} />
                  <TextField size="small" label="Costo" type="number" value={editCosto?.costo ?? 0} sx={{ width: 100 }}
                    onChange={e => setEditCosto(prev => ({ ...prev, costo: parseFloat(e.target.value) || 0 }))}
                    inputProps={{ step: '0.0001' }} />
                  <TextField size="small" label="Tipo Bono" value={editCosto?.tipoBono || ''} sx={{ width: 100 }}
                    onChange={e => setEditCosto(prev => ({ ...prev, tipoBono: e.target.value }))} />
                  <Button variant="contained" size="small" startIcon={<Save />} onClick={saveCosto}>Guardar</Button>
                  {editCosto?.idCostoActividad ? (
                    <Button size="small" onClick={() => setEditCosto(null)} startIcon={<Cancel />}>Cancelar</Button>
                  ) : null}
                </Box>
                <Box sx={{ overflowX: 'auto' }}>
                  <Table size="small">
                    <TableHead><TableRow>
                      <TableCell>Labor</TableCell><TableCell>Descripcion</TableCell>
                      <TableCell>Periodo</TableCell><TableCell align="right">Cantidad</TableCell>
                      <TableCell>U.Medida</TableCell><TableCell>Descr. U.M.</TableCell>
                      <TableCell align="right">Costo</TableCell><TableCell>Tipo Bono</TableCell>
                      <TableCell width={80}>Acc.</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {costos.map(c => (
                        <TableRow key={c.idCostoActividad}>
                          <TableCell>{c.labor?.codigo}</TableCell>
                          <TableCell>{c.labor?.descripcion}</TableCell>
                          <TableCell>{c.periodo}</TableCell>
                          <TableCell align="right">{c.cantidad?.toFixed(4)}</TableCell>
                          <TableCell>{c.unidadMedida}</TableCell>
                          <TableCell>{c.descripcionUM}</TableCell>
                          <TableCell align="right">{c.costo?.toFixed(6)}</TableCell>
                          <TableCell>{c.tipoBono}</TableCell>
                          <TableCell>
                            <IconButton size="small" onClick={() => setEditCosto(c)}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => deleteCosto(c.idCostoActividad)}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                      {costos.length === 0 && (
                        <TableRow><TableCell colSpan={9} align="center">No hay costos registrados para esta actividad</TableCell></TableRow>
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </>
            )}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MasterDataDialog;
